/**
 * URL normalization shared by the web app, the extension popup, and the
 * extension service worker. Users frequently save sites by typing just a
 * hostname ("example.com"), without a `https://` scheme. That bare string
 * isn't a valid URL — `new URL("example.com")` throws, `window.open` treats
 * it as a relative path, and `chrome.tabs.create` rejects it outright. The
 * helpers below let callers parse and launch URL fields tolerantly, so an
 * item stored with `url: "example.com"` still resolves to a correct hostname
 * and launches at `https://example.com`.
 */

// Captures the scheme of a `scheme://host` URL. Requires the `//` authority
// marker so a schemeless `host:port` (e.g. `localhost:8080`) is NOT mistaken
// for a scheme and instead gets `https://` prepended below.
const SCHEME_PREFIX_RE = /^([a-z][a-z0-9+.-]*):\/\//i;

// The only `scheme://` forms allowed to pass through verbatim. This is a
// security allowlist, not a convenience list: the result is rendered directly
// into `<a href>` and passed to `window.open`/`chrome.tabs.create`, so a
// `javascript:`/`data:`/`vbscript:`/`file:`/`blob:` URL would execute or read
// in the app's origin. Because document links and contact website fields are
// **shared, cross-user** content (authored by one family member, rendered in
// another's decrypted session), passing a dangerous scheme through is a
// stored-XSS sink. An allowlist (vs. a denylist of known-bad schemes) also
// blocks future/obscure executable schemes. See the security review, C1.
const SAFE_URL_SCHEMES = new Set(['http', 'https']);

/**
 * Returns the input with a leading `https://` prepended when no scheme is
 * present. If the input already carries an explicit `scheme://`, only `http`
 * and `https` pass through — any other scheme (`javascript:`, `data:`, etc.)
 * returns the empty string, because the result is used as an `<a href>` /
 * launch target. Trims surrounding whitespace; trailing slashes are NOT
 * stripped here — see `normalizeUrlForStorage` if a canonical-for-storage form
 * is needed. Returns the empty string for null/undefined/whitespace inputs so
 * callers can chain into `new URL(...)` without an extra guard.
 */
export function ensureUrlScheme(url: string | null | undefined): string {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  const match = SCHEME_PREFIX_RE.exec(trimmed);
  if (match) {
    // Explicit `scheme://` present — pass through only if it's http(s),
    // otherwise neutralize (empty href / no-op launch) to block scheme-based XSS.
    return SAFE_URL_SCHEMES.has(match[1].toLowerCase()) ? trimmed : '';
  }
  // Schemeless (bare host, host:port, or a no-authority `scheme:` like
  // `mailto:`/`data:` which becomes an inert `https://…` string) → default https.
  return `https://${trimmed}`;
}

/**
 * Like `new URL(url)` but tolerates inputs missing a scheme. Returns `null`
 * if the input is empty OR if parsing fails even after adding the default
 * scheme (e.g. nonsense like `:::` or empty hostnames). Callers can then
 * branch on null instead of catching exceptions at every parse site.
 */
export function tryParseUrl(url: string | null | undefined): URL | null {
  const normalized = ensureUrlScheme(url);
  if (!normalized) return null;
  try {
    return new URL(normalized);
  } catch {
    return null;
  }
}

/**
 * Host for equality comparisons: parse (adding https:// when schemeless via
 * tryParseUrl), strip a leading "www.", lowercase. Falls back to the lowercased
 * input when unparseable; "" for empty. Not a real eTLD+1 — sufficient for the
 * autofill duplicate/dedup comparison (tighter logic lives server-side).
 */
export function normalizedHost(url: string): string {
  if (!url) return '';
  const parsed = tryParseUrl(url);
  if (!parsed) return url.toLowerCase();
  return parsed.hostname.replace(/^www\./i, '').toLowerCase();
}

/**
 * Reduce a URL to its origin (scheme://host[:port]) for storage, dropping path,
 * query, and fragment. Tolerates schemeless input (adds https:// via
 * tryParseUrl) and only passes http(s) through (the same safety allowlist as
 * ensureUrlScheme). Returns the trimmed input unchanged if it cannot be parsed,
 * so a save is never lost or blanked. Used by clients when persisting an
 * auto-detected login's site — see docs/architecture/07-client-contract.md.
 */
export function normalizeUrlForStorage(url: string | null | undefined): string {
  const parsed = tryParseUrl(url);
  if (!parsed) return (url ?? '').trim();
  return parsed.origin;
}
